/**
 * Centralized date formatting helpers. Every user-facing date in the app
 * should go through one of these so we render dd/mm/yyyy consistently
 * (Romanian convention) instead of leaving each component to call
 * `toLocaleDateString` with whatever locale the browser picks.
 *
 * Why dd/mm/yyyy with slashes (not ro-RO's default dots): the client
 * asked for the form-style format that matches the inputs (which use
 * "/" separators), and "/" is also less ambiguous in scanning lists.
 */

const TIME_ZONE = "Europe/Bucharest";

function isInvalid(d: Date): boolean {
  return Number.isNaN(d.getTime());
}

/**
 * dd/mm/yyyy (or the given fallback when the value is missing / invalid).
 * Accepts ISO strings, Date instances, or null/undefined.
 */
export function formatDate(value: string | Date | null | undefined, fallback = "—"): string {
  if (value === null || value === undefined || value === "") return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (isInvalid(d)) return fallback;
  // en-GB gives dd/mm/yyyy with slash separators, matches our spec exactly.
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: TIME_ZONE,
  });
}

/**
 * dd/mm/yyyy, hh:mm.
 */
export function formatDateTime(value: string | Date | null | undefined, fallback = "—"): string {
  if (value === null || value === undefined || value === "") return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (isInvalid(d)) return fallback;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: TIME_ZONE,
  });
}

/**
 * Short form: dd/mm (no year), useful for dense calendar views.
 */
export function formatDateShort(value: string | Date | null | undefined, fallback = "—"): string {
  if (value === null || value === undefined || value === "") return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (isInvalid(d)) return fallback;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    timeZone: TIME_ZONE,
  });
}

/**
 * Convert a Date or ISO string into the value an <input type="date">
 * expects: yyyy-mm-dd. Always the same regardless of locale — the
 * input itself renders dd/mm/yyyy because the document is lang="ro".
 */
export function toDateInputValue(value: string | Date | null | undefined): string {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (isInvalid(d)) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Convert a Date or ISO string into the value an <input type="datetime-local">
 * (and our DateInput mode="datetime") expects: yyyy-mm-ddTHH:mm in LOCAL time.
 *
 * Not interchangeable with `toISOString().slice(0, 16)`, which yields UTC wall
 * time. Feeding that to a local-time input shifts the value by the UTC offset
 * — in Romania that silently moved a session two or three hours earlier every
 * time someone opened and saved the edit form.
 */
export function toDateTimeInputValue(value: string | Date | null | undefined): string {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (isInvalid(d)) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
